import { formatDate } from "@/modules/leads/utils";
import { TaskStatuses } from "@/modules/tasks/enums";
import { LinkedEntity } from "@/modules/tasks/dtos";
import { SystemTaskTypes } from "@/modules/tasks/enums";
import { containsArabic } from "@/modules/common/utils";
import { TaskStatusesBackend } from "@/modules/tasks/enums";
import { convertLocalDateToUTC } from "@/modules/tasks/utils/timezone-conversion";

// Smart date formatting function
export const formatSmartDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const inputDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const time = date
    .toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  if (inputDate.getTime() === today.getTime()) {
    return `Today, ${time}`;
  } else if (inputDate.getTime() === yesterday.getTime()) {
    return `Yesterday, ${time}`;
  } else if (inputDate.getTime() === tomorrow.getTime()) {
    return `Tomorrow, ${time}`;
  } else {
    return formatDate(dateString);
  }
};

export const getTaskStatus = (status: string, dueDate: string) => {
  const date = new Date(dueDate);
  if (status === TaskStatusesBackend.pending && date <= new Date()) {
    return TaskStatuses.Overdue;
  } else if (status === TaskStatusesBackend.pending) {
    return TaskStatuses.Open;
  } else if (status === TaskStatusesBackend.archived) {
    return TaskStatuses.Archived;
  } else {
    return TaskStatuses.Completed;
  }
};

export const checkIfTaskIsOverdue = (status: string, dueDate: string) => {
  const date = new Date(dueDate);
  if (status === TaskStatusesBackend.pending && date <= new Date()) {
    return true;
  } else {
    return false;
  }
};

export const checkIfTaskIsSubmittedOverdue = (
  status: string,
  dueDate: string,
  submittedDate: string | null
) => {
  const date = new Date(dueDate);
  const submitted = new Date(submittedDate || "");
  if (status === TaskStatusesBackend.pending && date <= new Date()) {
    return true;
  } else if (
    status === TaskStatusesBackend.completed &&
    submitted &&
    submitted >= date
  ) {
    return true;
  } else {
    return false;
  }
};

// Helper function to combine date and time
export const combineDateAndTime = (
  date: Date | null,
  time: string
): Date | null => {
  if (!date) return null;

  // Get the date components (year, month, day)
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  if (time && time.trim()) {
    const [hoursStr, minutesStr] = time.split(":");
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (!isNaN(hours) && !isNaN(minutes)) {
      const combined = new Date(year, month, day, hours, minutes, 0, 0);
      return combined;
    }
  }

  // If no valid time provided, create date at start of day
  const combined = new Date(year, month, day, 0, 0, 0, 0);
  return combined;
};

// Helper function to capitalize the first letter of a string
export const capitalizeFirstLetter = (
  text: string | undefined | null
): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const formatBidirectionalText = (
  text: string,
  suffix?: string,
  separator: string = " - "
): string => {
  if (!text) return "";

  const isArabic = containsArabic(text);

  // Bidirectional control characters
  const RLM = "\u200F"; // Right-to-Left Mark
  const LRM = "\u200E"; // Left-to-Right Mark

  if (!suffix) {
    return isArabic ? `${RLM}${text}${RLM}` : text;
  }

  //  "Name - ID
  return isArabic
    ? `${RLM}${text}${RLM}${separator}${LRM}${suffix}${LRM}`
    : `${text}${separator}${suffix}`;
};
//UTC date conversion
export const getCommonDates = () => {
  const dateNow = new Date();
  const now = dateNow.toISOString();
  const todayStart = convertLocalDateToUTC(dateNow, true);
  const todayEnd = convertLocalDateToUTC(dateNow, false);

  return {
    now,
    todayStart,
    todayEnd,
  };
};

export const setToStartOfDayUTC = (dateStr: string): string => {
  const datePart = dateStr.split("T")[0];
  const [year, month, day] = datePart.split("-");

  // Create date from the parts
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  // Use timezone-aware conversion
  return convertLocalDateToUTC(date, true);
};

export const setToEndOfDayUTC = (dateStr: string): string => {
  // Extract just the date part (YYYY-MM-DD)
  const datePart = dateStr.split("T")[0];
  const [year, month, day] = datePart.split("-");

  // Create date from the parts
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  // Use timezone-aware conversion
  return convertLocalDateToUTC(date, false);
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getTodayTasks = () => {
  return {
    dueDateMin: getCommonDates().todayStart,
    dueDateMax: getCommonDates().todayEnd,
    status: TaskStatusesBackend.pending,
  };
};

export const getStartOfWeekUTC = (): string => {
  const dateNow = new Date();
  const dayOfWeek = dateNow.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const daysToSubtract = dayOfWeek; // Subtract days to get to Sunday
  const startOfWeek = new Date(dateNow);
  startOfWeek.setDate(startOfWeek.getDate() - daysToSubtract);
  startOfWeek.setHours(0, 0, 0, 0);
  return convertLocalDateToUTC(startOfWeek, true);
};

export const getUpcomingTasks = () => {
  return {
    dueDateMin: getStartOfWeekUTC(),
    status: TaskStatusesBackend.pending,
  };
};

export const getCompletedTasks = () => {
  return {
    status: TaskStatusesBackend.completed,
  };
};

export const getOverdueTasks = () => {
  return {
    dueDateMax: getCommonDates().now,
    status: TaskStatusesBackend.pending,
  };
};

export const mapTypeToDisplay = (type?: string | null) => {
  if (!type) return "";
  if (type == SystemTaskTypes.Meeting_subordinate) return "one to one meeting";
  return type;
};

export const excludeTaskTypes = () => {
  return {
    excludeTypes: [SystemTaskTypes.Meeting_subordinate],
  };
};

/**
 * Builds a task content message with linked entity context for AI evaluation.
 */
export const buildTaskContentWithContext = (
  taskTitle: string,
  linkedEntities?: LinkedEntity[]
): string => {
  if (!linkedEntities || linkedEntities.length === 0) {
    return taskTitle;
  }

  const linkedEntity = linkedEntities[0];
  const entityType = linkedEntity.metaData?.type;
  const entityId = linkedEntity.metaData?.id;

  if (entityType === "lead" && entityId) {
    return `${taskTitle} (Note: this task is related to Lead #${entityId})`;
  }

  if (entityType === "property" && entityId) {
    return `${taskTitle} (Note: this task is related to Property #${entityId})`;
  }

  return taskTitle;
};
